import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import {
  Login,
  ForgotPassword,
  SuccessForgotPasswordEmail,
  CreateOrResetPassword,
  SuccessCreateOrResetPassword,
} from './../modules/login';
import { Unsubscribe } from '../modules/unsubscribe/unsubscribe';
import { UnsubscribeFromMonthlyReminders } from '../modules/unsubscribeFromReminders/unsubsribeFromMonthlyReminders';
import {
  TamariskDocumentDocusignCallback,
} from '@roc/feature-tamarisk-payments';
import { SecureLinkCreateScopeOfWorkPage, SecureLinkScopeOfWorkPage } from '../modules/scopeOfWorkV2';
import { useAppRoutes, useStore } from '../hooks';
import { PasswordChangeType } from '../types';
import { BodyLayout, Page } from '@roc/feature-app-core';
import {
  CreditAuthorization,
  CreditAuthorizationDocusignCallback,
} from '../modules/creditAuthorization';
import {
  WireAuthorization,
  WireAuthorizationDocusignCallback,
} from '../modules/wireAuthorization';
import { insertIf, isHausUrl, isLocalHost, isPrivateLenderPortalUrl, isFacoUrl, isCivicUrl, isRocCapitalUrl } from '@roc/client-portal-shared/utils';
import { isProduction } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import DenialOfCredit from '../modules/denialOfCredit/denialOfCredit';
import DrawRequest from '../modules/drawRequest/drawRequest';
import { CookiePolicy, PrivacyPolicy, TermsAndConditions } from '../modules/policies/policies';
import { AutoLogin } from '../modules/login/components/autoLogin';
import { DocusignCallback } from '../modules/docusignCallback/docusignCallback';
import { useDocumentLibraryRoutes } from '@roc/feature-document-library-contentful';
import { ScopeOfWorkLinkReplacedPage, SecureLinkScopeOfWorkRevisionPage } from '../modules/scopeofWorkRevision';
import ManageNotifications from '../modules/manageNotifications/component/manageNotifications';
import { BorrowerDetailsVerification, BorrowerDetailsVerificationDocusignCallback, BorrowerDetailsVerificationReminderEmail } from '@roc/feature-borrowers';
import { Login2FA } from '../modules/login/login2FA';
import { Logout } from '../modules/logout/logout';
import { AchLinkReplacedPage, ACHDocusign, LiquidityVerificationLinkReplacedPage, SecureLinkACHFormPage } from '@roc/feature-plaid';
import { WireInstructionsLinkReplacedPage, WireInstructionsPage } from '../modules/wireInstructions/component/wireInstructionsPage';
import { FundingDocs } from '../modules/fundingDocs';
import { VerifyBankInformationPage } from '../modules/verifyBankInformation/component/verifyBankInformationPage';
import MobileTruliooVerification from '../modules/login/mobileTruliooVerification';
import { getValuationReviewInternalRoutes } from '@roc/feature-valuation-review-design';
import { ReferralFeeLinkReplacedPage, SecureLinkReferralFeePage } from '@roc/feature-referral';
import { PublicBorrowerSetupDocusignCallback } from 'libs/feature-borrowers/src/borrowerSetupToDo';
import { PublicBorrowerSetupToDo } from '../modules/borrowerSetupToDo/publicBorrowerSetupToDo';
import {
  LoanFastrackDocumentDocusignCallback,
  LoanFastrackLinkReplacedPage,
  SecureLinkLoanFastrackPaymentPage,
} from '@roc/feature-loan-fasttrack';
import { ExtensionAgreementSigning } from '../modules/extensionAgreement/extensionAgreementSigning';
import { BorrowerFixFlipApplication, LandingPage, PublicContainerWithLogo, BorrowerSinglePropertyRentalLoanApplication, BorrowerGroundUpApplication } from '@roc/feature-borrower-loan-submission';
import { PublicLoanSubmissionPlatformUi } from '../modules/loanApplication/publicLoanSubmissionPlatformUi';
import { PublicScopeOfWorkForm } from '@roc/feature-sow-v2';
import { CAF_PDF_HTML } from '../../../../../libs/feature-borrowers/src/caf/caf-pdf';
import { CAF_WITH_EMAIL_PHONE_VERIFICATION } from '../../../../../libs/feature-borrowers/src/caf/caf-with-email-phone-verification';
import { BackgroundPull } from '../../../../../libs/feature-borrowers/src/caf/backgroundPull';
import { PublicCAF } from '../modules/caf/publicCaf';
import { IdVerification } from '../modules/identityCheck/idVerification';
import InternalValuationDocumentDocusignCallback from '../modules/internalValuation/internalValuationDocumentDocusignCallback';
import { ValuationPaymentContainer } from '../modules/valuationPaymentModule/valuationPaymentContainer';
import { Survey } from '../modules/survey/survey';
import { SurveyUnsubscribe } from '../modules/survey/surveyUnsubscribe';

const getPublicRoutes = (publicRoutes, allowBgCreditStaticLink: boolean) => {
  return [
    ...insertIf(isLocalHost() || isHausUrl() || isPrivateLenderPortalUrl() || isFacoUrl() || isCivicUrl() || isRocCapitalUrl(), [
      <Route exact path={publicRoutes.privacyPoicy.path}>
        <Page routeDefinition={publicRoutes.privacyPoicy}>
          <PrivacyPolicy />
        </Page>
      </Route>,
      <Route exact path={publicRoutes.cookiePoicy.path}>
        <Page routeDefinition={publicRoutes.cookiePoicy}>
          <CookiePolicy />
        </Page>
      </Route>,
      <Route exact path={publicRoutes.termsAndConditions.path}>
        <Page routeDefinition={publicRoutes.termsAndConditions}>
          <TermsAndConditions />
        </Page>
      </Route>,
    ]),
    <Route exact path={publicRoutes.login.path}>
      <Page routeDefinition={publicRoutes.login}>
        <Login />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.unsubscribe.path}>
      <Page routeDefinition={publicRoutes.unsubscribe}>
        <Unsubscribe />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.unsubscribeFromMonthlyReminders.path}>
      <Page routeDefinition={publicRoutes.unsubscribeFromMonthlyReminders.path}>
        <UnsubscribeFromMonthlyReminders />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.twoFactorAuthentication.path}>
      <Page routeDefinition={publicRoutes.twoFactorAuthentication}>
        <Login2FA />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.logout.path}>
      <Page routeDefinition={publicRoutes.logout}>
        <Logout />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.forgotpassword.path}>
      <Page routeDefinition={publicRoutes.forgotpassword}>
        <ForgotPassword />
      </Page>
    </Route>,
    // <Route exact path={publicRoutes.createBorrowerAccount.path}>
    //   <Page routeDefinition={publicRoutes.createBorrowerAccount}>
    //     <CreateBorrowerAccount />
    //   </Page>
    // </Route>,
    // <Route exact path={publicRoutes.createBorrowerAccountNoParam.path}>
    //   <Page routeDefinition={publicRoutes.createBorrowerAccountNoParam}>
    //     <CreateBorrowerAccount />
    //   </Page>
    // </Route>,
    // <Route exact path={publicRoutes.registerBorrowerSuccessful.path}>
    //   <Page routeDefinition={publicRoutes.registerBorrowerSuccessful}>
    //     <RegistrationSuccessfulScreen />
    //   </Page>
    // </Route>,
    // ...insertIf(isRegistrationEnabled, [
    //   <Route exact path={publicRoutes.register.path}>
    //     <Page routeDefinition={publicRoutes.register}>
    //       <Register />
    //     </Page>
    //   </Route>,
    //   <Route exact path={publicRoutes.successRegistration.path}>
    //     <Page routeDefinition={publicRoutes.successRegistration}>
    //       <SuccessNewUserRegistration />
    //     </Page>
    //   </Route>,
    // ]),
    <Route exact path={publicRoutes.successforgotpasswordemail.path}>
      <Page routeDefinition={publicRoutes.successforgotpasswordemail}>
        <SuccessForgotPasswordEmail />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.updatepassword.path}>
      <Page routeDefinition={publicRoutes.updatepassword}>
        <CreateOrResetPassword passwordChangeType={PasswordChangeType.UPDATE} />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.successupdatepassword.path}>
      <Page routeDefinition={publicRoutes.successupdatepassword}>
        <SuccessCreateOrResetPassword
          passwordChangeType={PasswordChangeType.UPDATE}
        />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.createpassword.path}>
      <Page routeDefinition={publicRoutes.createpassword}>
        <CreateOrResetPassword passwordChangeType={PasswordChangeType.CREATE} />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.createpasswordMigratedUser.path}>
      <Page routeDefinition={publicRoutes.createpasswordMigratedUser}>
        <CreateOrResetPassword
          passwordChangeType={PasswordChangeType.CREATE_MIGRATED_USER}
        />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.successcreatepassword.path}>
      <Page routeDefinition={publicRoutes.successcreatepassword}>
        <SuccessCreateOrResetPassword
          passwordChangeType={PasswordChangeType.CREATE}
        />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.valuationPayments.path}>
      <ValuationPaymentContainer />
    </Route>,
    <Route exact path={publicRoutes.loanFastrackPayments.path}>
      <LoanFastrackLinkReplacedPage />
    </Route>,
    <Route exact path={publicRoutes.secureLinkLoanFastrackPayment.path}>
      <SecureLinkLoanFastrackPaymentPage />
    </Route>,
    <Route exact path={publicRoutes.extensionAgreementSigning.path}>
      <ExtensionAgreementSigning />
    </Route>,
    <Route exact path={publicRoutes.tamariskDocumentDocusignCallback.path}>
      <TamariskDocumentDocusignCallback />
    </Route>,
    <Route exact path={publicRoutes.internalValuationDocumentDocusignCallback.path}>
      <InternalValuationDocumentDocusignCallback />
    </Route>,
    <Route exact path={publicRoutes.loanFastrackDocumentDocusignCallback.path}>
      <LoanFastrackDocumentDocusignCallback />
    </Route>,
    <Route exact path={publicRoutes.creditAuthorization.path}>
      <Page routeDefinition={publicRoutes.creditAuthorization}>
        <CreditAuthorization />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.wireAuthorization.path}>
      <Page routeDefinition={publicRoutes.wireAuthorization}>
        <WireAuthorization />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.mobileTruliooVerification.path}>
      <Page routeDefinition={publicRoutes.mobileTruliooVerification}>
        <MobileTruliooVerification />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.wireAuthorizationDocusignCallback.path}>
      <Page routeDefinition={publicRoutes.wireAuthorizationDocusignCallback}>
        <WireAuthorizationDocusignCallback />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.creditAuthorizationDocusignCallback.path}>
      <Page routeDefinition={publicRoutes.creditAuthorizationDocusignCallback}>
        <CreditAuthorizationDocusignCallback />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.scopeOfWorkV2.path}>
      <Page routeDefinition={publicRoutes.scopeOfWorkV2}>
        <ScopeOfWorkLinkReplacedPage />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.secureLinkScopeOfWork.path}>
      <Page routeDefinition={publicRoutes.secureLinkScopeOfWork}>
        <SecureLinkScopeOfWorkPage />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.createScopeOfWork.path}>
      <Page routeDefinition={publicRoutes.createScopeOfWork}>
        <ScopeOfWorkLinkReplacedPage />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.secureLinkCreateScopeOfWork.path}>
      <Page routeDefinition={publicRoutes.secureLinkCreateScopeOfWork}>
        <SecureLinkCreateScopeOfWorkPage />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.createScopeOfWorkV2.path}>
      <Page routeDefinition={publicRoutes.createScopeOfWorkV2}>
        <PublicScopeOfWorkForm />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.denialOfCredit.path}>
      <Page routeDefinition={publicRoutes.denialOfCredit}>
        <DenialOfCredit />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.drawRequest.path}>
      <Page routeDefinition={publicRoutes.drawRequest}>
        <DrawRequest />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.docusignCallback.path}>
      <Page routeDefinition={publicRoutes.docusignCallback}>
        <DocusignCallback />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.scopeOfWorkRevision.path}>
      <Page routeDefinition={publicRoutes.scopeOfWorkRevision}>
        <ScopeOfWorkLinkReplacedPage />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.secureLinkScopeOfWorkRevision.path}>
      <Page routeDefinition={publicRoutes.secureLinkScopeOfWorkRevision}>
        <SecureLinkScopeOfWorkRevisionPage />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.manageEmailSubscriptions.path}>
      <Page routeDefinition={publicRoutes.manageEmailSubscriptions}>
        <ManageNotifications />
      </Page>
    </Route>,
    // <Route exact path={publicRoutes.borrowerDetailsVerification.path}>
    //   <Page routeDefinition={publicRoutes.borrowerDetailsVerification}>
    //     <BorrowerDetailsVerification />
    //   </Page>
    // </Route>,
    // <Route exact path={publicRoutes.borrowerDetailsVerificationDocusignCallback.path}>
    //   <Page routeDefinition={publicRoutes.borrowerDetailsVerificationDocusignCallback}>
    //     <BorrowerDetailsVerificationDocusignCallback />
    //   </Page>
    // </Route>,
    <Route exact path={publicRoutes.unsubscribedFromDailyReminders.path}>
      <Page routeDefinition={publicRoutes.unsubscribedFromDailyReminders}>
        <BorrowerDetailsVerificationReminderEmail />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.loanApplication.path}>
      <Page routeDefinition={publicRoutes.loanApplication}>
        <LandingPage currentRoutes={publicRoutes.loanApplication} />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.loanApplication.children.fixFlip.path}>
      <Page routeDefinition={publicRoutes.loanApplication.children.fixFlip}>
        <BorrowerFixFlipApplication currentRoutes={publicRoutes.loanApplication} isFromBorrowerPortal={false} />
      </Page>
    </Route>,
    // <Route exact path={publicRoutes.loanApplication.children.groundUp.path}>
    //   <Page routeDefinition={publicRoutes.loanApplication.children.groundUp}>
    //     <BorrowerGroundUpApplication currentRoutes={publicRoutes.loanApplication} />
    //   </Page>
    // </Route>,
    <Route exact path={publicRoutes.loanApplication.children.singleProperty.path}>
      <Page routeDefinition={publicRoutes.loanApplication.children.singleProperty}>
        <BorrowerSinglePropertyRentalLoanApplication currentRoutes={publicRoutes.loanApplication} isFromBorrowerPortal={false} />
      </Page>
    </Route>,
    <Route
      exact
      path={[
        publicRoutes.applyForALoan.children.fixFlip.path,
        publicRoutes.applyForALoan.children.groundUp.path,
        publicRoutes.applyForALoan.children.singleProperty.path,
        publicRoutes.applyForALoan.path,
      ]}
    >
      <PublicContainerWithLogo>
        <PublicLoanSubmissionPlatformUi
          currentRoutes={publicRoutes.applyForALoan}
        />
      </PublicContainerWithLogo>
    </Route>,
    <Route exact path={publicRoutes.achFormDocusignCallback.path}>
      <ACHDocusign />
    </Route>,
    <Route exact path={publicRoutes.achForm.path}>
      <AchLinkReplacedPage />
    </Route>,
    <Route exact path={publicRoutes.liquidityVerification.path}>
      <LiquidityVerificationLinkReplacedPage />
    </Route>,
    <Route exact path={publicRoutes.secureLinkAchForm.path}>
      <SecureLinkACHFormPage />
    </Route>,
    <Route exact path={publicRoutes.referralFeeAuth.path}>
      <ReferralFeeLinkReplacedPage />
    </Route>,
    <Route exact path={publicRoutes.secureLinkReferralFee.path}>
      <SecureLinkReferralFeePage />
    </Route>,
    <Route exact path={publicRoutes.borrowerDetailsVerification.path}>
      <Page routeDefinition={publicRoutes.borrowerDetailsVerification}>
        <PublicBorrowerSetupToDo />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.borrowerDetailsVerificationDocusignCallback.path}>
      <Page routeDefinition={publicRoutes.borrowerDetailsVerificationDocusignCallback}>
        <PublicBorrowerSetupDocusignCallback />
      </Page>
    </Route>,
    ...insertIf(allowBgCreditStaticLink, [
      <Route exact path={publicRoutes.softCreditPull.path}>
        <Page routeDefinition={publicRoutes.softCreditPull}>
          <PublicCAF />
        </Page>
      </Route>,
      <Route exact path={publicRoutes.backgroundPull.path}>
        <Page routeDefinition={publicRoutes.backgroundPull}>
          <BackgroundPull />
        </Page>
      </Route>
    ]),
    <Route exact path={publicRoutes.softCreditPullWithParams.path}>
      <Page routeDefinition={publicRoutes.softCreditPullWithParams}>
        <PublicCAF />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.backgroundPullWithParams.path}>
      <Page routeDefinition={publicRoutes.backgroundPullWithParams}>
        <BackgroundPull />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.credit_auth_unused.path}>
      <Page routeDefinition={publicRoutes.credit_auth_unused}>
        <CAF_WITH_EMAIL_PHONE_VERIFICATION />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.idVerification.path}>
      <Page routeDefinition={publicRoutes.idVerification}>
        <IdVerification />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.surveyUnsubscribe.path}>
      <Page routeDefinition={publicRoutes.surveyUnsubscribe}>
        <SurveyUnsubscribe />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.survey.path}>
      <Page routeDefinition={publicRoutes.survey}>
        <Survey />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.wireInstructions.path}>
      <WireInstructionsLinkReplacedPage />
    </Route>,
    <Route exact path={publicRoutes.secureLinkWireInstructions.path}>
      <WireInstructionsPage />
    </Route>,
    <Route exact path={publicRoutes.fundingDocs.path}>
      <Page routeDefinition={publicRoutes.fundingDocs}>
        <FundingDocs />
      </Page>
    </Route>,
    <Route exact path={publicRoutes.verifyBankInformation.path}>
      <VerifyBankInformationPage />
    </Route>,
  ];
};
export const UnAuthenticatedView = observer(() => {
  const { publicRoutes } = useAppRoutes();
  const { globalStore, registerStore } = useStore();

  const getRedirectToUrl = () => {
    let _url = publicRoutes.login.url;
    return globalStore.redirectPath
      ? `${_url}?redirectPath=${globalStore.redirectPath}`
      : _url;
  };

  const { documentLibraryRoutes } = useDocumentLibraryRoutes();
  const routes = [
    ...getPublicRoutes(publicRoutes, globalStore?.portalConfiguration?.allowBgCreditStaticLink),
    documentLibraryRoutes.documentLibraryDocument,
    documentLibraryRoutes.documentLibraryDocumentPreview
  ];

  if (!isProduction) {
    const valuationReviewInternalRoutes = getValuationReviewInternalRoutes(globalStore.routeBasePath, globalStore.routeBaseUrl);
    routes.push(valuationReviewInternalRoutes.valuationReview);
    routes.push(valuationReviewInternalRoutes.valuationReview2);
  }

  return (
    <BodyLayout>
      <Switch>
        <Route exact path={["/", "/autologin"]}>
          <AutoLogin />
        </Route>
        {React.Children.toArray(routes.map(x => x))}
        <Route
          render={() => {
            return <Redirect to={getRedirectToUrl()} />;
          }}
        />
      </Switch>
    </BodyLayout>
  );
});
